import { useCallback, useEffect, useState } from 'react';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { getInitialOrCaptureContactId } from '@/components/amazon-connect/contactUtils';
import { callService } from '@/services/callService';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { notifyIncomingContact } from '@/utils/desktopNotification';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { api } from '@/store/api';
import {
  clearPendingAcceptedWarmConsult,
  setCachedConsultContext,
  setIsConsultCall,
} from '@/store/slices/warmTransferSlice';
import { IncomingCallModal } from './IncomingCallModal';
import { WarmTransferIncomingModal } from '@/components/contact-center/WarmTransferIncomingModal';
import type { WarmTransferActiveContext } from '@/types/warmTransfer';
import type { ConnectContact } from '@/types';

type IncomingMode = 'probing' | 'customer' | 'warm';

function getContactIds(contact: ConnectContact): string[] {
  return [
    contact.getContactId?.(),
    getInitialOrCaptureContactId(contact),
    connectAdapter.getCallScopeContactId(contact),
  ].filter((id): id is string => Boolean(id));
}

/**
 * Incoming voice/chat: probe warm-transfer context by initialContactId.
 * 404 → normal customer incoming; 200 → warm consult modal.
 */
export function IncomingCallModalContainer() {
  const dispatch = useAppDispatch();
  const pendingAcceptedWarmConsult = useAppSelector(
    state => state.warmTransfer.pendingAcceptedWarmConsult
  );
  const { activeContacts, incomingContact, setIncomingContact } =
    useAmazonConnectContacts();
  const isCallbackFromMetadata = useAppSelector(
    state => state.call.contactInformation?.isCallback ?? false
  );
  const callerPhoneNumber = useAppSelector(
    state => state.call.contactInformation?.callerPhoneNumber ?? null
  );
  // Prefer the contact type (available immediately) over metadata (async fetch).
  // For queue_callback contacts the badge appears instantly instead of flickering
  // in after /call/metadata resolves.
  let incomingContactType: string | undefined;
  try {
    incomingContactType = incomingContact?.getType?.();
  } catch {
    // Contact may already be destroyed remotely (StateError).
    incomingContactType = undefined;
  }
  const isCallback =
    incomingContactType === 'queue_callback' || isCallbackFromMetadata;
  const [fetchContext] = api.useLazyGetActiveTransferContextQuery();
  const [respondTransfer] = api.useRespondAgentTransferMutation();
  const [cancelWarmTransferSession] = api.useCancelWarmTransferMutation();
  const [markWarmConsultConnected] = api.useMarkWarmConsultConnectedMutation();

  const [mode, setMode] = useState<IncomingMode>('probing');
  const [warmContext, setWarmContext] =
    useState<WarmTransferActiveContext | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [isCallbackAccepted, setIsCallbackAccepted] = useState(false);

  // Desktop notification when an incoming call/chat arrives in the background.
  // Re-fires with the same contact id tag when the phone number arrives, so the
  // OS replaces the earlier toast instead of stacking duplicates.
  useEffect(() => {
    if (!incomingContact) return;
    let contactId: string | null = null;
    let isChat = false;
    try {
      contactId = incomingContact.getContactId();
      isChat = connectAdapter.isChatContact(incomingContact);
    } catch {
      return; // contact already destroyed remotely
    }
    if (!contactId) return;
    notifyIncomingContact(contactId, isChat, callerPhoneNumber);
  }, [incomingContact, callerPhoneNumber]);

  useEffect(() => {
    if (!incomingContact) {
      console.log('[AC-Container] incomingContact null -> probing');
      setMode('probing');
      setWarmContext(null);
      setIsCallbackAccepted(false);
      return;
    }

    const contactType = incomingContact.getType?.();
    const contactIds = getContactIds(incomingContact);
    console.log(
      '[AC-Container] incomingContact set',
      JSON.stringify({
        contactId: incomingContact.getContactId?.(),
        contactType,
        contactIds,
        isCallback,
        isCallbackAccepted,
      })
    );

    if (connectAdapter.isChatContact(incomingContact)) {
      console.log('[AC-Container] chat -> customer mode');
      setMode('customer');
      setWarmContext(null);
      return;
    }

    const initialContactId = contactIds[contactIds.length - 1]!;
    const matchesPendingWarmConsult =
      Boolean(pendingAcceptedWarmConsult) &&
      contactIds.includes(pendingAcceptedWarmConsult!.initialContactId);
    console.log('[AC-Container] probing warm transfer', initialContactId);
    setMode('probing');
    void fetchContext({ initialContactId })
      .unwrap()
      .then(data => {
        console.log('[AC-Container] warm transfer context found -> warm mode');
        setWarmContext(data);
        setMode('warm');
      })
      .catch(() => {
        if (matchesPendingWarmConsult || pendingAcceptedWarmConsult) {
          console.log('[AC-Container] pending warm consult match -> warm mode');
          setWarmContext({
            initialContactId:
              pendingAcceptedWarmConsult?.initialContactId ?? initialContactId,
            mode: 'warm',
            status: 'accepted',
            initiatedByConnectId:
              pendingAcceptedWarmConsult?.fromConnectId ?? '',
            initiatedByDisplayName:
              pendingAcceptedWarmConsult?.fromDisplayName ?? null,
            targetType: 'spog_agent',
            requestId: null,
            context: pendingAcceptedWarmConsult?.context ?? {},
          });
          setMode('warm');
          return;
        }
        console.log('[AC-Container] no warm transfer -> customer mode');
        setWarmContext(null);
        setMode('customer');
      });
    // isCallback / isCallbackAccepted only used for logging above — adding them
    // would re-probe warm transfer on every accept state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingContact, fetchContext, pendingAcceptedWarmConsult]);

  const handleCustomerAccept = () => {
    if (!incomingContact) return;
    console.log('[AC-Container] handleCustomerAccept clicked', {
      isCallback,
      isCallbackAccepted,
    });
    const isChat = connectAdapter.isChatContact(incomingContact);

    // Callbacks: accept the contact to start ringing the customer,
    // then transition to the "dialing customer" timer state.
    if (isCallback) {
      console.log('[AC-Container] callback accept -> calling contact.accept()');
      const contact = incomingContact;
      connectAdapter.acceptContact(contact, {
        success: () => {
          console.log('[AC-Container] callback accept SUCCESS -> timer');
          setIsCallbackAccepted(true);
        },
        failure: (err?: Error) => {
          console.error('[AC-Container] callback accept FAILED', err);
          // Still show the timer — Connect may still be dialing even if
          // accept fails (e.g. if auto-accept kicked in first).
          setIsCallbackAccepted(true);
        },
      });
      return;
    }

    const contact = incomingContact;
    // Outbound calls have no Boost metadata — flag the stream so the backend
    // skips the Boost fetch. Same derivation as NotesCard.
    const isOutboundCall = Boolean(contact && !contact.isInbound());
    connectAdapter.acceptContact(contact, {
      success: () => {
        setIncomingContact(null);
        if (!isChat) {
          const contactId = connectAdapter.getCallScopeContactId(contact);
          callService.initiateCall(contactId, { isOutbound: isOutboundCall });
        }
      },
      failure: () => {},
    });
  };

  const handleCustomerDecline = () => {
    if (!incomingContact) return;
    connectAdapter.rejectContact(incomingContact, {
      success: () => setIncomingContact(null),
      failure: () => {},
    });
  };

  const handleOutboundCancel = () => {
    if (!incomingContact) return;
    const contact = incomingContact;
    // Destroying the agent connection hangs up a dialing outbound call.
    connectAdapter.destroyAgentConnection(contact, {
      success: () => setIncomingContact(null),
      failure: () => setIncomingContact(null),
    });
  };

  const handleWarmAccept = useCallback(() => {
    if (!incomingContact || !warmContext) return;
    setInFlight(true);
    const contact = incomingContact;
    const alreadyAccepted =
      Boolean(warmContext.requestId) &&
      warmContext.requestId === pendingAcceptedWarmConsult?.requestId;

    const finishAccept = () => {
      dispatch(setCachedConsultContext(warmContext.context));
      dispatch(setIsConsultCall(true));
      const consultContactId = contact.getContactId();
      const streamContactId = warmContext.initialContactId;
      // The stream targets the customer contact (initialContactId), not the
      // consult contact — derive isOutbound from that contact. Same derivation
      // as NotesCard; falls back to false when the contact is not active.
      const streamContact = activeContacts.find(c => {
        try {
          return (
            c.getContactId() === streamContactId ||
            getInitialOrCaptureContactId(c) === streamContactId
          );
        } catch {
          return false;
        }
      });
      const isOutboundCall = Boolean(
        streamContact && !streamContact.isInbound()
      );
      console.log('[WarmTransfer] starting consult transcript stream', {
        consultContactId,
        streamContactId,
        requestId: warmContext.requestId ?? null,
      });
      void callService.initiateCall(streamContactId, {
        mode: 'warm_consult',
        initialContactId: warmContext.initialContactId,
        warmTransferRequestId: warmContext.requestId ?? null,
        isOutbound: isOutboundCall,
      });
      if (
        warmContext.targetType === 'queue' ||
        warmContext.targetType === 'spog_agent'
      ) {
        console.log('[WarmTransfer] mark consult connected', {
          targetType: warmContext.targetType,
          initialContactId: warmContext.initialContactId,
          consultContactId,
        });
        void markWarmConsultConnected({
          initialContactId: warmContext.initialContactId,
        }).catch(error => {
          console.error('[WarmTransfer] consult-connected call failed', error);
        });
      }
      if (
        pendingAcceptedWarmConsult?.initialContactId ===
        warmContext.initialContactId
      ) {
        dispatch(clearPendingAcceptedWarmConsult());
      }
      setIncomingContact(null);
      setWarmContext(null);
      setMode('probing');
      setInFlight(false);
    };

    if (warmContext.requestId && !alreadyAccepted) {
      void respondTransfer({
        requestId: warmContext.requestId,
        decision: 'accept',
      })
        .unwrap()
        .then(() => {
          connectAdapter.acceptContact(contact, {
            success: finishAccept,
            failure: () => setInFlight(false),
          });
        })
        .catch(() => setInFlight(false));
    } else {
      connectAdapter.acceptContact(contact, {
        success: finishAccept,
        failure: () => setInFlight(false),
      });
    }
  }, [
    incomingContact,
    warmContext,
    pendingAcceptedWarmConsult,
    markWarmConsultConnected,
    dispatch,
    setIncomingContact,
    respondTransfer,
    activeContacts,
  ]);

  const handleWarmDecline = useCallback(() => {
    if (!incomingContact || !warmContext) return;
    setInFlight(true);
    const contact = incomingContact as ConnectContact;
    const initialContactId = warmContext.initialContactId;

    const finishDecline = () => {
      void cancelWarmTransferSession({
        initialContactId,
        reason: 'declined_by_agent2',
      });
      if (pendingAcceptedWarmConsult?.initialContactId === initialContactId) {
        dispatch(clearPendingAcceptedWarmConsult());
      }
      setIncomingContact(null);
      setWarmContext(null);
      setMode('probing');
      setInFlight(false);
    };

    if (warmContext.requestId) {
      void respondTransfer({
        requestId: warmContext.requestId,
        decision: 'decline',
      })
        .unwrap()
        .finally(() => {
          connectAdapter.rejectContact(contact, {
            success: finishDecline,
            failure: finishDecline,
          });
        });
    } else {
      connectAdapter.rejectContact(contact, {
        success: finishDecline,
        failure: finishDecline,
      });
    }
  }, [
    incomingContact,
    warmContext,
    pendingAcceptedWarmConsult,
    cancelWarmTransferSession,
    dispatch,
    setIncomingContact,
    respondTransfer,
  ]);

  if (!incomingContact) return null;

  if (mode === 'warm') {
    return (
      <WarmTransferIncomingModal
        open
        context={warmContext}
        loading={false}
        inFlight={inFlight}
        alreadyAccepted={
          pendingAcceptedWarmConsult?.initialContactId ===
          warmContext?.initialContactId
        }
        onAccept={handleWarmAccept}
        onDecline={handleWarmDecline}
      />
    );
  }

  if (mode === 'customer') {
    return (
      <IncomingCallModal
        contact={incomingContact}
        isChat={connectAdapter.isChatContact(incomingContact)}
        isCallback={isCallback}
        isCallbackAccepted={isCallbackAccepted}
        onAccept={handleCustomerAccept}
        onDecline={handleCustomerDecline}
        onCancel={handleOutboundCancel}
      />
    );
  }

  return null;
}
